import {createRef, useMemo, useRef, useState} from 'react';

import type {User} from 'nutshell-core/users/users-types';

import type {Tab} from 'shells/control-bar/page-control-bar';
import {getWidgetIconForType} from 'shells/widgets/helpers';

import type {ContactPageFragment as Contact} from 'nutshell-graphql-types';

import {MINI_HEADER_HEIGHT_WITHOUT_FORM} from '../../../../core-entity/mini-header/mini-header';
import {
    getWidgetComponents,
    getWidgetFallbackLabel,
} from '../../../../core-entity/util/widget-components';
import {TimelineWidget} from '../../../../core-entity/widgets/timeline-widget';
import {getWidgetTabCount} from '../../../../core-entity/workspace/helpers';
import {WorkspaceSection as EntityWorkspaceSection} from '../../../../core-entity/workspace/workspace-section';
import {useGetContactEnrichment} from '../../../../nutshell-intelligence-enrichment/contact-enrichment/graphql/hooks';
import {
    formatData,
    shouldShowData,
} from '../../../../nutshell-intelligence-enrichment/contact-enrichment/helpers';

import {ContactPeopleIqBanner} from '../people-iq/contact-people-iq-banner';

import {useGetWidgetCounts} from './helpers';

type Props = {
    contact: Contact;
    scrollContainerRef: HTMLDivElement | null | undefined;
    isSidebarCollapsed: boolean;
    isWorkspaceSidebarCollapsed: boolean;
    onIsWorkspaceSidebarCollapsedChange: (isCollapsed: boolean) => void;
    user: User;
    hasFullHeightSidebar?: boolean;
};

export function WorkspaceSection(props: Props) {
    const {
        contact,
        scrollContainerRef,
        isSidebarCollapsed,
        isWorkspaceSidebarCollapsed,
        onIsWorkspaceSidebarCollapsedChange,
        hasFullHeightSidebar,
    } = props;

    const workspaceSectionRef = useRef<HTMLDivElement>(null);
    const [isContactEnrichmentDismissed, setIsContactEnrichmentDismissed] =
        useState<boolean>(false);

    const widgetCounts = useGetWidgetCounts(contact.id);
    const {contactEnrichment} = useGetContactEnrichment({id: contact.id});
    const {mainFields, additionalFields} = formatData(contactEnrichment);
    const shouldShowContactEnrichment = Boolean(
        contactEnrichment && shouldShowData(mainFields, additionalFields)
    );

    const workspaceWidgets = useMemo(() => {
        return contact.workspaceWidgets ?? [];
    }, [contact.workspaceWidgets]);

    const visibleWidgetObjects = useMemo(() => {
        return workspaceWidgets.filter((widget) => !widget.isHidden);
    }, [workspaceWidgets]);

    const dynamicWorkspaceWidgetComponents = getWidgetComponents({
        mode: 'contact',
        entityId: contact.id,
        widgets: visibleWidgetObjects,
        user: props.user,
    });

    const enrichmentWidget = useMemo(() => {
        if (!shouldShowContactEnrichment || !contactEnrichment || isContactEnrichmentDismissed) {
            return null;
        }

        return (
            <ContactPeopleIqBanner
                key='contact-people-iq'
                contactId={contact.id}
                contactName={contact.name}
                contactEnrichment={contactEnrichment}
                onDismiss={() => setIsContactEnrichmentDismissed(true)}
            />
        );
    }, [
        contact.id,
        contact.name,
        contactEnrichment,
        isContactEnrichmentDismissed,
        shouldShowContactEnrichment,
    ]);

    const displayedWidgetComponents = useMemo(() => {
        return [
            ...(enrichmentWidget ? [enrichmentWidget] : []),
            ...dynamicWorkspaceWidgetComponents,
            <TimelineWidget
                key='timeline'
                entityId={contact.id}
                entityName='Contacts'
                scrollContainerRef={scrollContainerRef}
                hideCreator={true}
                useContainerScroll={true}
            />,
        ];
    }, [contact.id, enrichmentWidget, dynamicWorkspaceWidgetComponents, scrollContainerRef]);

    const tabOptions: Array<Tab> = useMemo(() => {
        return [
            ...visibleWidgetObjects.map((widget) => ({
                id: widget.id,
                label: getWidgetFallbackLabel(widget.type, widget.name),
                icon: getWidgetIconForType(widget.type),
                count: getWidgetTabCount(widget.type, widgetCounts),
                hideTooltip: !isSidebarCollapsed,
            })),
            {
                id: 'timeline',
                label: 'Timeline',
                icon: getWidgetIconForType('TIMELINE'),
                hideTooltip: !isSidebarCollapsed,
            },
        ];
    }, [isSidebarCollapsed, visibleWidgetObjects, widgetCounts]);

    const tabRefsMap = useRef<Record<string, React.RefObject<HTMLDivElement>>>({});
    const tabRefs = useMemo(() => {
        return tabOptions.reduce<Record<string, React.RefObject<HTMLDivElement>>>((map, tab) => {
            const existing = tabRefsMap.current[tab.id];
            if (existing) {
                map[tab.id] = existing;
            } else {
                const refObj = createRef<HTMLDivElement>();
                tabRefsMap.current[tab.id] = refObj;
                map[tab.id] = refObj;
            }

            return map;
        }, {});
    }, [tabOptions]);

    return (
        <EntityWorkspaceSection
            tabs={tabOptions}
            widgets={displayedWidgetComponents}
            isCollapsed={isWorkspaceSidebarCollapsed}
            onIsCollapsedChange={onIsWorkspaceSidebarCollapsedChange}
            workspaceSectionRef={workspaceSectionRef}
            scrollContainerRef={scrollContainerRef}
            tabRefs={tabRefs}
            stickyHeaderOffset={MINI_HEADER_HEIGHT_WITHOUT_FORM}
            hasFullHeightSidebar={hasFullHeightSidebar}
        />
    );
}
